import { CityState, Coordinate } from './types';

export class TrafficEngine {
  /**
   * Simulates daily commutes between residential areas (homes) and commercial/industrial zones (workplaces).
   * Calculates path-by-path road utilizations and generates traffic congestion grid data.
   */
  public tickTraffic(state: CityState, roadEngine: any) {
    // 1. Reset traffic grid
    state.trafficGrid = {};

    const homes: Coordinate[] = [];
    const jobs: Coordinate[] = [];

    // 2. Identify homes and workplaces
    for (const key of Object.keys(state.buildings)) {
      const b = state.buildings[key];
      if (b.stage === 'completed') {
        if (b.zone === 'residential' && b.occupancy > 0) {
          homes.push({ x: b.x, y: b.y });
        } else if ((b.zone === 'commercial' || b.zone === 'industrial' || b.zone === 'office') && b.occupancy > 0) {
          jobs.push({ x: b.x, y: b.y });
        }
      }
    }

    if (homes.length === 0 || jobs.length === 0) {
      return; // No active commutes possible
    }

    // 3. Select a representative sample of commutes to keep performance blazing fast (max 25 commutes)
    const commuteCount = Math.min(25, homes.length);
    const sampledHomes = homes.sort(() => Math.random() - 0.5).slice(0, commuteCount);

    const roadTraversals: Record<string, number> = {};

    for (const home of sampledHomes) {
      // Find the closest available workplace (commutes prefer shorter routes!)
      let closestJob: Coordinate | null = null;
      let minDist = Infinity;

      // Sample a subset of jobs for performance (max 10)
      const jobSample = jobs.sort(() => Math.random() - 0.5).slice(0, 10);

      for (const job of jobSample) {
        const dist = Math.abs(home.x - job.x) + Math.abs(home.y - job.y);
        if (dist < minDist) {
          minDist = dist;
          closestJob = job;
        }
      }

      if (closestJob) {
        // Find adjacent road keys for pathfinding (buildings sit adjacent to road frontage)
        const path = roadEngine.findPath(home.x, home.y, closestJob.x, closestJob.y);
        
        if (path && path.length > 0) {
          // Increment road traversal counters for every node in the path
          path.forEach((node: Coordinate) => {
            const key = `${node.x},${node.y}`;
            roadTraversals[key] = (roadTraversals[key] || 0) + 1;
          });
        }
      }
    }

    // 4. Map traversals into grid congestion rates (between 0 and 100)
    for (const key of Object.keys(state.roads)) {
      const traversals = roadTraversals[key] || 0;
      
      // 0 traversals = 0% traffic
      // 1 traversal = 15% traffic
      // 2 traversals = 40% traffic
      // 3 traversals = 70% traffic
      // 4+ traversals = 100% traffic (severe congestion!)
      let congestion = 0;
      if (traversals === 1) congestion = 15;
      else if (traversals === 2) congestion = 40;
      else if (traversals === 3) congestion = 75;
      else if (traversals >= 4) congestion = 100;

      if (congestion > 0) {
        state.trafficGrid[key] = congestion;
      }
    }
  }
}
